import { Credentials } from "@/domain/common/Credentials";
import * as Axios from 'axios';
import store from '@/store';
import { User } from "@/domain/User";
import { CookieService } from "./CookieService";

export default class AuthenticationService {
    private cookieService = new CookieService();

    public async login(credentials: Credentials): Promise<string> {
        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'post',
                url: `${store.state.apiBase}/public/token`,
                data: {
                    ...credentials
                }
            }
            const res: Axios.AxiosResponse = await Axios.default(apiConfig);

            // Store the token in the cookie (not refresh token - we use the same token)
            this.cookieService.setCookie("product-cloud", res.data.token, 30);

            return res.data.token;
        } catch (e) {
            const err = e as any;

            if (err.message === 'Network Error') throw Error('Authentication Server Unavailable');
            if (err.response?.status === 401) throw Error(err.response.data.message || 'Incorrect username or password');
            if (err.response?.status === 403) throw Error('Incorrect username or password');
            if (err.response?.status === 500) throw Error('Server Error');
            else throw Error(`Unhandled Error: ${err.message}`);
        }
    }

    public async silentLogin(): Promise<string> {
        const token = this.cookieService.getCookie("product-cloud");

        if (!token) throw Error('No valid token available');

        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'post',
            url: `${store.state.apiBase}/public/token/refresh`,
            data: {
                token
            }
        }

        const res: Axios.AxiosResponse = await Axios.default(apiConfig);
        this.cookieService.setCookie("product-cloud", res.data.token, 30);

        return res.data.token;
    }

    public async logout(): Promise<void> {
        const token = this.cookieService.getCookie("product-cloud");

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'post',
                url: `${store.state.apiBase}/public/token/revoke`,
                data: {
                    token
                }
            }
            await Axios.default(apiConfig);

            this.cookieService.deleteCookie('product-cloud');
        } catch (e) {
            // Even if revoke fails, delete the cookie
            this.cookieService.deleteCookie('product-cloud');

            const err = e as any;
            if (err.message === 'Network Error') throw Error('Authentication Server Unavailable');
            if (err.response?.status === 500) throw Error('Server Error');
        }
    }

    public async resetPassword(username: string): Promise<string> {
        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'post',
                url: `${store.state.apiBase}/public/password/reset`,
                data: {
                    email: username
                }
            }
            const res: Axios.AxiosResponse = await Axios.default(apiConfig);

            return res.data.response;
        } catch (e) {
            const err = e as any;
            if (err.message === 'Network Error') throw Error('Authentication Server Unavailable');
            else throw Error(`Unhandled Error: ${err.message}`);
        }
    }

    public async getUser(): Promise<User> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${store.state.apiBase}/pc/private/user`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await Axios.default(apiConfig)
        return res.data as User;
    }
}
